import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Award, Users, Globe, TrendingUp, Coins, ShieldCheck, Heart, Activity,
  Printer, Download, RefreshCw, Star, Sparkles, FileText,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, LineChart, Line, PieChart, Pie, Cell,
} from 'recharts';
import { adminService } from '../services/adminService';
import type {
  NarrativeOverview, NarrativeGeographyItem, NarrativeCohortRow, NarrativeItem,
} from '../types';
import { formatCompactNumber, formatNumber, formatPercent, formatDateTime } from '../utils/format';
import './AdminPage.css';
import './VisaBrief.css';

const COLORS = ['#410C6D', '#22c55e', '#f59e0b', '#ec4899', '#5a1494', '#10b981', '#ef4444'];

function StatCard({
  icon, label, value, hint,
}: { icon: React.ReactNode; label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="vb-stat">
      <div className="vb-stat-icon">{icon}</div>
      <div className="vb-stat-body">
        <span className="vb-stat-label">{label}</span>
        <span className="vb-stat-value">{value}</span>
        {hint && <span className="vb-stat-hint">{hint}</span>}
      </div>
    </div>
  );
}

// Lightweight equirectangular bubble map.
// World box uses lon: [-180, 180], lat: [-60, 75] for a friendly framing.
function WorldBubbles({ items }: { items: NarrativeGeographyItem[] }) {
  const width = 760;
  const height = 360;
  const lonMin = -180; const lonMax = 180;
  const latMin = -60; const latMax = 75;
  const latLngToXY = (lat: number, lon: number): [number, number] => {
    const x = ((lon - lonMin) / (lonMax - lonMin)) * width;
    const y = height - ((lat - latMin) / (latMax - latMin)) * height;
    return [x, y];
  };
  const maxUsers = Math.max(1, ...items.map((d) => d.users));
  return (
    <div className="vb-map-wrap">
      <svg viewBox={`0 0 ${width} ${height}`} className="vb-map">
        {/* background */}
        <rect x={0} y={0} width={width} height={height} className="vb-map-bg" />
        {/* graticule */}
        {Array.from({ length: 7 }).map((_, i) => {
          const y = (i + 1) * (height / 8);
          return <line key={`h${i}`} x1={0} x2={width} y1={y} y2={y} className="vb-map-grid" />;
        })}
        {Array.from({ length: 11 }).map((_, i) => {
          const x = (i + 1) * (width / 12);
          return <line key={`v${i}`} x1={x} x2={x} y1={0} y2={height} className="vb-map-grid" />;
        })}
        {items.map((d) => {
          if (!d.latlng) return null;
          const [lat, lon] = d.latlng;
          const [x, y] = latLngToXY(lat, lon);
          const r = 4 + (d.users / maxUsers) * 22;
          return (
            <g key={d.country_code}>
              <circle cx={x} cy={y} r={r} className="vb-bubble" />
              <text x={x} y={y + 3} textAnchor="middle" className="vb-bubble-label">
                {d.country_code}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

const VisaBrief: React.FC = () => {
  const [days, setDays] = useState(90);
  const [includeTest, setIncludeTest] = useState(false);
  const [includeCalibration, setIncludeCalibration] = useState(true);
  const [overview, setOverview] = useState<NarrativeOverview | null>(null);
  const [geography, setGeography] = useState<NarrativeGeographyItem[]>([]);
  const [cohorts, setCohorts] = useState<NarrativeCohortRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    const params = { days, include_test: includeTest, include_calibration: includeCalibration };
    const [ov, geo, coh] = await Promise.allSettled([
      adminService.getNarrativeOverview(params),
      adminService.getNarrativeGeography({ include_test: includeTest, include_calibration: includeCalibration }),
      adminService.getNarrativeCohorts(12),
    ]);
    if (ov.status === 'fulfilled') setOverview(ov.value);
    if (geo.status === 'fulfilled') setGeography(geo.value?.items || []);
    if (coh.status === 'fulfilled') setCohorts(coh.value?.rows || []);
    setLoading(false);
    setRefreshing(false);
  }, [days, includeTest, includeCalibration]);

  useEffect(() => { load(); }, [load]);

  const handlePrint = () => window.print();

  const handleExportJson = () => {
    if (!overview) return;
    const payload = {
      overview,
      geography,
      cohorts,
      exported_at: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `trinze-visa-brief-${stamp}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}${window.location.pathname}?days=${days}&asof=${encodeURIComponent(new Date().toISOString())}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).catch(() => { /* ignore */ });
    }
  };

  // Charts -------------------------------------------------------------
  const signupSeries = useMemo(() => (overview?.growth.weekly_signups || []).map((w) => ({
    week: w.week_start.slice(5),
    signups: w.signups,
  })), [overview]);

  const cohortSeries = useMemo(() => cohorts.map((c) => ({
    week: c.week_start.slice(5),
    Calibration: c.calibration,
    Production: c.production,
  })), [cohorts]);

  const genderData = useMemo(() => {
    if (!overview) return [];
    return Object.entries(overview.diversity.gender_distribution)
      .map(([name, value]) => ({ name, value }));
  }, [overview]);

  const ageData = useMemo(() => {
    if (!overview) return [];
    const order = ['18-24', '25-34', '35-44', '45-54', '55-64', '65+', 'unknown'];
    return order
      .filter((k) => overview.diversity.age_distribution[k] !== undefined)
      .map((k) => ({ band: k, users: overview.diversity.age_distribution[k] || 0 }));
  }, [overview]);

  const languageData = useMemo(() => {
    if (!overview) return [];
    return Object.entries(overview.diversity.language_distribution)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => (b.value as number) - (a.value as number))
      .slice(0, 6);
  }, [overview]);

  const kycData = useMemo(() => {
    if (!overview) return [];
    const order = ['tier_0', 'tier_1', 'tier_2', 'tier_3'];
    return order
      .filter((k) => overview.compliance.kyc_distribution[k] !== undefined)
      .map((k) => ({ tier: k.replace('tier_', 'T'), users: overview.compliance.kyc_distribution[k] }));
  }, [overview]);

  if (loading || !overview) {
    return (
      <div className="admin-page visa-brief">
        <div className="page-header">
          <div>
            <h1><FileText size={20} style={{ marginRight: 8 }} />Investor / Visa Brief</h1>
            <p className="page-subtitle">Loading aggregated narrative…</p>
          </div>
        </div>
        <div className="skeleton" style={{ height: 200 }} />
      </div>
    );
  }

  const { reach, growth, economic_impact, innovation, compliance, featured_items } = overview;

  return (
    <div className="admin-page visa-brief">
      <div className="page-header no-print">
        <div>
          <h1><FileText size={20} style={{ marginRight: 8 }} />Investor / Visa Brief</h1>
          <p className="page-subtitle">
            Curated, print-friendly narrative for visa, investor and partner reviews.
            Generated {formatDateTime(overview.generated_at)} · window {overview.window_days}d.
          </p>
        </div>
        <div className="header-actions">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
            <option value={365}>Last 365 days</option>
          </select>
          <label className="vb-toggle">
            <input type="checkbox" checked={includeCalibration} onChange={(e) => setIncludeCalibration(e.target.checked)} />
            include calibration
          </label>
          <label className="vb-toggle">
            <input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} />
            include test
          </label>
          <button className="btn secondary" onClick={load} disabled={refreshing}>
            <RefreshCw size={14} className={refreshing ? 'spin' : ''} /> {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          <button className="btn secondary" onClick={handleCopyLink}>Copy link</button>
          <button className="btn secondary" onClick={handleExportJson}>
            <Download size={14} /> Export JSON
          </button>
          <button className="btn" onClick={handlePrint}>
            <Printer size={14} /> Print / Save PDF
          </button>
        </div>
      </div>

      {/* Print-only header */}
      <div className="vb-print-header print-only">
        <div>
          <h1>Trinze — Pre-launch traction & impact</h1>
          <p>As of {formatDateTime(overview.generated_at)} · {overview.window_days}-day window</p>
        </div>
        <div className="vb-print-stamp">CONFIDENTIAL</div>
      </div>

      {/* Hero stats */}
      <section className="vb-hero">
        <StatCard
          icon={<Users size={20} />}
          label="Users"
          value={formatCompactNumber(reach.total_users)}
          hint={`${formatNumber(reach.active_users_30d)} active in last 30d`}
        />
        <StatCard
          icon={<Globe size={20} />}
          label="Countries"
          value={formatNumber(reach.country_count)}
          hint={`${formatNumber(reach.language_count)} languages`}
        />
        <StatCard
          icon={<Activity size={20} />}
          label="Sessions"
          value={formatCompactNumber(innovation.games_analysed)}
          hint={`${formatPercent(innovation.flag_rate_pct, 2)} flagged`}
        />
        <StatCard
          icon={<Coins size={20} />}
          label="Player payouts (window)"
          value={`${formatCompactNumber(economic_impact.window_payouts_tp)} TP`}
          hint={`${formatNumber(economic_impact.unique_paid_users)} unique paid users`}
        />
        <StatCard
          icon={<ShieldCheck size={20} />}
          label="Avg trust score"
          value={innovation.average_trust_score != null ? innovation.average_trust_score.toFixed(1) : '—'}
          hint="Bio-Kinetic engine"
        />
        <StatCard
          icon={<Sparkles size={20} />}
          label="UK calibration cohort"
          value={formatCompactNumber(reach.calibration_users)}
          hint="Pre-launch UK pilot"
        />
      </section>

      {/* Reach */}
      <section className="vb-section">
        <header><h2><Globe size={18} /> Reach</h2></header>
        <p className="vb-section-copy">
          Trinze has onboarded users across {reach.country_count} countries and {reach.language_count} languages.
          The map below visualises distribution by country; bubble size scales with user count.
        </p>
        <div className="vb-reach-grid">
          <div className="vb-map-card">
            <WorldBubbles items={geography} />
          </div>
          <div className="panel">
            <div className="panel-header"><h3>Top countries</h3></div>
            <div className="panel-body tight">
              <table className="data-table">
                <thead>
                  <tr><th>Country</th><th>Users</th><th>Sessions</th></tr>
                </thead>
                <tbody>
                  {reach.top_countries.map((c) => {
                    const geo = geography.find((g) => g.country_code === c.country_code);
                    return (
                      <tr key={c.country_code}>
                        <td>{c.country_code} · {c.country_name}</td>
                        <td>{formatNumber(c.users)}</td>
                        <td>{formatNumber(geo?.sessions || 0)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="vb-mini-stats">
          <span><strong>{formatNumber(reach.device_split.android)}</strong> Android</span>
          <span><strong>{formatNumber(reach.device_split.ios)}</strong> iOS</span>
          <span><strong>{formatNumber(reach.device_split.web_or_unknown)}</strong> Web/other</span>
        </div>
      </section>

      {/* Growth */}
      <section className="vb-section">
        <header><h2><TrendingUp size={18} /> Growth</h2></header>
        <p className="vb-section-copy">
          Trinze is in pre-launch with traction concentrated in a tightly-controlled calibration
          cohort. Week-over-week signup growth and time-to-first-action ratios validate engagement
          quality at small scale.
        </p>
        <div className="vb-growth-grid">
          <div className="panel">
            <div className="panel-header"><h3>Weekly signups (12w)</h3></div>
            <div className="panel-body">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={signupSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="signups" stroke="#410C6D" strokeWidth={2} dot />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="panel">
            <div className="panel-header"><h3>Calibration vs production cohort</h3></div>
            <div className="panel-body">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={cohortSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="week" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Calibration" stackId="a" fill="#410C6D" />
                  <Bar dataKey="Production" stackId="a" fill="#22c55e" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div className="vb-mini-stats">
          <span><strong>{formatNumber(growth.dau)}</strong> DAU</span>
          <span><strong>{formatNumber(growth.wau)}</strong> WAU</span>
          <span><strong>{formatNumber(growth.mau)}</strong> MAU</span>
          <span><strong>{growth.wow_growth_pct != null ? `${growth.wow_growth_pct}%` : '—'}</strong> WoW signup growth</span>
          <span><strong>{growth.viral_coefficient_pct != null ? `${growth.viral_coefficient_pct}%` : '—'}</strong> referrer share</span>
          <span><strong>{growth.avg_time_to_first_session_hours != null ? `${growth.avg_time_to_first_session_hours}h` : '—'}</strong> avg time to first session</span>
        </div>
      </section>

      {/* Economic impact */}
      <section className="vb-section">
        <header><h2><Coins size={18} /> Economic impact</h2></header>
        <p className="vb-section-copy">
          Trinze rewards users for verified attention and skill. Below is a snapshot of value
          flowing to players across the {overview.window_days}-day window.
        </p>
        <div className="vb-kpi-row">
          <StatCard icon={<TrendingUp size={18} />} label="Window stakes" value={formatCompactNumber(economic_impact.window_stakes)} />
          <StatCard icon={<Coins size={18} />} label="Window payouts (TP)" value={formatCompactNumber(economic_impact.window_payouts_tp)} />
          <StatCard icon={<Award size={18} />} label="Completed cashouts" value={formatCompactNumber(economic_impact.completed_withdrawals_total)} />
          <StatCard icon={<Users size={18} />} label="Unique paid users" value={formatNumber(economic_impact.unique_paid_users)} />
          <StatCard icon={<Heart size={18} />} label="Outstanding redeemable (TP)" value={formatCompactNumber(economic_impact.redeemable_outstanding_tp)} />
          <StatCard icon={<Star size={18} />} label="Vaulted credits (TP)" value={formatCompactNumber(economic_impact.vaulted_credits_tp)} />
        </div>
      </section>

      {/* Innovation */}
      <section className="vb-section">
        <header><h2><ShieldCheck size={18} /> Innovation & trust</h2></header>
        <div className="vb-narrative">
          <p>
            <strong>Bio-Kinetic Fraud Engine.</strong> Every session is scored in real time against
            a cocktail of behavioural, kinetic, device and provenance signals. Sub-human and
            sub-bot patterns are blocked or shadow-flagged before they pollute reward economics.
          </p>
          <p>
            <strong>Ad-Integration Service (AIS).</strong> A single abstraction multiplexes
            offerwall, rewarded video and survey inventory across multiple providers
            (AdGem / TheoremReach / RapidoReach / BitLabs / Pollfish), with deterministic
            fail-over and fraud-aware callback verification.
          </p>
          <p>
            <strong>Skill-based gaming compliance.</strong> Quizzes and recall tasks are gated
            into payouts so the platform demonstrably qualifies as skill-based — a defensible
            posture for UK / EU launch.
          </p>
        </div>
        <div className="vb-mini-stats">
          <span><strong>{formatCompactNumber(innovation.games_analysed)}</strong> games analysed</span>
          <span><strong>{formatCompactNumber(innovation.flagged_games)}</strong> flagged</span>
          <span><strong>{formatPercent(innovation.flag_rate_pct, 2)}</strong> flag rate</span>
          <span><strong>{innovation.average_trust_score != null ? innovation.average_trust_score.toFixed(1) : '—'}</strong> avg trust score</span>
        </div>
      </section>

      {/* Compliance */}
      <section className="vb-section">
        <header><h2><Award size={18} /> Compliance & UK pilot</h2></header>
        <p className="vb-section-copy">
          Trinze is operating a calibration cohort in the UK ahead of full launch.
          Calibration users transact in non-redeemable Testnet TP so the platform can
          validate skill-based posture, KYC processes and partner integrations
          without a live money flow.
        </p>
        <div className="vb-compliance-grid">
          <div className="panel">
            <div className="panel-header"><h3>KYC tier distribution</h3></div>
            <div className="panel-body">
              {kycData.length === 0 ? (
                <div className="empty">No KYC data yet</div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={kycData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="tier" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="users" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <div className="panel vb-spotlight">
            <div className="panel-header"><h3>UK calibration cohort</h3></div>
            <div className="panel-body">
              <div className="vb-spotlight-num">{formatCompactNumber(reach.calibration_users)}</div>
              <p>{compliance.calibration_cohort.note}</p>
              <ul>
                <li>Average quiz accuracy: <strong>{compliance.average_quiz_accuracy_pct != null ? `${compliance.average_quiz_accuracy_pct}%` : '—'}</strong></li>
                <li>Skill-correct ratio: <strong>{formatPercent(compliance.skill_correct_pct, 1)}</strong></li>
                <li>Skill quiz attempts: <strong>{formatCompactNumber(compliance.skill_quiz_attempts)}</strong></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Diversity */}
      <section className="vb-section">
        <header><h2><Users size={18} /> Diversity & inclusion</h2></header>
        <div className="vb-diversity-grid">
          <div className="panel">
            <div className="panel-header"><h3>Gender</h3></div>
            <div className="panel-body">
              {genderData.length === 0 ? <div className="empty">No data</div> : (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={genderData} dataKey="value" nameKey="name" outerRadius={75} label>
                      {genderData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <div className="panel">
            <div className="panel-header"><h3>Age band</h3></div>
            <div className="panel-body">
              {ageData.length === 0 ? <div className="empty">No data</div> : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={ageData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="band" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="users" fill="#5a1494" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <div className="panel">
            <div className="panel-header"><h3>Languages</h3></div>
            <div className="panel-body">
              {languageData.length === 0 ? <div className="empty">No data</div> : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={languageData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" width={60} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Press & Accolades */}
      <section className="vb-section">
        <header><h2><Star size={18} /> Press, partnerships & milestones</h2></header>
        {featured_items.length === 0 ? (
          <div className="empty">No featured narrative items yet — add some via the Press page.</div>
        ) : (
          <div className="vb-press-grid">
            {featured_items.map((item: NarrativeItem) => (
              <article key={item.id} className="vb-press-card">
                {item.image_url && <img src={item.image_url} alt="" />}
                <div className="vb-press-body">
                  <span className={`pill ${item.type}`}>{item.type}</span>
                  <h4>{item.title}</h4>
                  {item.body && <p>{item.body}</p>}
                  <div className="vb-press-meta">
                    {item.source && <span>{item.source}</span>}
                    {item.occurred_at && <span>{formatDateTime(item.occurred_at)}</span>}
                    {item.url && <a href={item.url} target="_blank" rel="noreferrer">Open →</a>}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="vb-footer">
        <p>Trinze · Confidential · Generated {formatDateTime(overview.generated_at)} · {overview.include_calibration ? 'incl. calibration' : 'excl. calibration'} · {overview.include_test ? 'incl. test' : 'excl. test'}</p>
      </footer>
    </div>
  );
};

export default VisaBrief;
